import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Search, Ticket, Download, Loader2, Sun, Moon, ArrowLeft, Mail, Hash } from 'lucide-react';
import SenArtisteLogo from '../components/SenArtisteLogo';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FindTickets = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  
  const [searchMethod, setSearchMethod] = useState('email'); // 'email' or 'ticketNumber'
  const [email, setEmail] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [searched, setSearched] = useState(false);
  
  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (searchMethod === 'email' && !email) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    if (searchMethod === 'ticketNumber' && !ticketNumber) {
      toast.error('Veuillez entrer le numéro de billet');
      return;
    }
    
    setLoading(true);
    setSearched(false);
    
    try {
      let response;
      if (searchMethod === 'email') {
        response = await axios.get(`${API_URL}/tickets/find-by-email`, {
          params: { email: email.trim().toLowerCase() }
        });
      } else {
        response = await axios.get(`${API_URL}/tickets/find-by-number/${ticketNumber.trim().toUpperCase()}`);
      }
      
      const ticketData = response.data;
      setTickets(Array.isArray(ticketData) ? ticketData : [ticketData]);
      setSearched(true);
      
      if ((Array.isArray(ticketData) && ticketData.length === 0) || !ticketData) {
        toast.info('Aucun billet trouvé');
      } else {
        toast.success(`${Array.isArray(ticketData) ? ticketData.length : 1} billet(s) trouvé(s)`);
      }
    } catch (error) {
      console.error('Error finding tickets:', error);
      setSearched(true);
      setTickets([]);
      if (error.response?.status === 404) {
        toast.error('Aucun billet trouvé avec ces informations');
      } else {
        toast.error('Erreur lors de la recherche');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const downloadTicketPDF = async (ticket) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(212, 175, 55);
    doc.rect(0, 0, 210, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('SEN ARTISTE', 105, 20, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Votre billet électronique', 105, 32, { align: 'center' });
    
    // Ticket info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(ticket.eventTitle || 'Événement', 105, 60, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`N° ${ticket.ticketNumber || ticket.id.slice(0, 8).toUpperCase()}`, 105, 75, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${ticket.eventDate ? new Date(ticket.eventDate).toLocaleDateString('fr-FR') : 'N/A'}`, 20, 95);
    doc.text(`Lieu: ${ticket.eventLocation || 'N/A'}`, 20, 105);
    doc.text(`Acheteur: ${ticket.buyerName}`, 20, 115);
    doc.text(`Email: ${ticket.buyerEmail}`, 20, 125);
    
    const priceText = ticket.currency === 'EUR' 
      ? `${ticket.ticketPrice?.toFixed(2) || '0.00'}€`
      : `${ticket.ticketPrice?.toLocaleString('fr-FR') || 0} F CFA`;
    doc.text(`Prix: ${priceText}`, 20, 135);
    
    // QR Code
    const qrData = ticket.ticketNumber || ticket.id;
    const qrDataUrl = await QRCode.toDataURL(qrData, { width: 150 });
    doc.addImage(qrDataUrl, 'PNG', 70, 145, 70, 70);
    
    // Code
    doc.setFontSize(10);
    doc.text(`Code: ${ticket.ticketNumber || ticket.id}`, 105, 225, { align: 'center' });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Présentez ce QR code à l\'entrée de l\'événement', 105, 240, { align: 'center' });
    doc.text('Ce billet est personnel et non transférable', 105, 250, { align: 'center' });
    
    doc.save(`billet-${ticket.ticketNumber || ticket.id.slice(0, 8)}.pdf`);
    toast.success('Billet téléchargé !');
  };
  
  return (
    <div className="min-h-screen py-8 px-4 hero-gradient">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </Button>
      
      <div className="max-w-xl mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <SenArtisteLogo className="w-12 h-12" />
            <span className="font-syne text-xl font-bold">Sen Artiste</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-syne text-3xl font-bold mb-2">Retrouver mes billets</h1>
            <p className="text-muted-foreground">
              Recherchez vos billets par email ou par numéro de billet
            </p>
          </motion.div>
        </div>
        
        {/* Search Form */}
        <Card className="glass-card mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Méthode de recherche</CardTitle>
            <CardDescription>
              Choisissez comment retrouver vos billets
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              {/* Method Toggle */}
              <div className="flex gap-2 p-1 bg-muted rounded-lg">
                <Button
                  type="button"
                  variant={searchMethod === 'email' ? 'default' : 'ghost'}
                  className="flex-1"
                  onClick={() => setSearchMethod('email')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Par email
                </Button>
                <Button
                  type="button"
                  variant={searchMethod === 'ticketNumber' ? 'default' : 'ghost'}
                  className="flex-1"
                  onClick={() => setSearchMethod('ticketNumber')}
                >
                  <Hash className="w-4 h-4 mr-2" />
                  Par numéro
                </Button>
              </div>
              
              {/* Email Input */}
              {searchMethod === 'email' && (
                <div className="space-y-2">
                  <Label htmlFor="email">Email utilisé lors de l'achat</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              )}
              
              {/* Ticket Number Input */}
              {searchMethod === 'ticketNumber' && (
                <div className="space-y-2">
                  <Label htmlFor="ticketNumber">Numéro de billet</Label>
                  <Input
                    id="ticketNumber"
                    type="text"
                    placeholder="TKT-XXXXXXXX"
                    value={ticketNumber}
                    onChange={(e) => setTicketNumber(e.target.value.toUpperCase())}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Le numéro se trouve sur votre email de confirmation
                  </p>
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Rechercher mes billets
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Results */}
        {searched && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {tickets.length > 0 ? (
              <Card className="glass-card mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Ticket className="w-5 h-5 text-primary" />
                      {tickets.length} billet(s) trouvé(s)
                    </span>
                    {tickets.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => tickets.forEach(t => downloadTicketPDF(t))}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Tout télécharger
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tickets.map((ticket, index) => (
                    <div 
                      key={ticket.id} 
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg border gap-3"
                    >
                      <div className="flex-1">
                        <p className="font-bold text-lg">{ticket.eventTitle || 'Événement'}</p>
                        <p className="text-sm font-mono bg-primary/10 px-2 py-1 rounded inline-block mt-1">
                          {ticket.ticketNumber || ticket.id.slice(0, 8).toUpperCase()}
                        </p>
                        <div className="text-sm text-muted-foreground mt-2">
                          {ticket.eventDate && (
                            <p>Date: {new Date(ticket.eventDate).toLocaleDateString('fr-FR')}</p>
                          )}
                          <p>Acheteur: {ticket.buyerName}</p>
                          <p>Statut: <span className={ticket.status === 'VALID' ? 'text-green-600' : 'text-red-600'}>
                            {ticket.status === 'VALID' ? 'Valide' : ticket.status === 'USED' ? 'Utilisé' : ticket.status}
                          </span></p>
                        </div>
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => downloadTicketPDF(ticket)}
                        className="w-full sm:w-auto"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger PDF
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card className="glass-card mb-6">
                <CardContent className="p-8 text-center">
                  <Ticket className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">Aucun billet trouvé</p>
                  <p className="text-muted-foreground text-sm">
                    Vérifiez que l'email ou le numéro de billet est correct
                  </p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}
        
        {/* Back Button */}
        <div className="text-center">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FindTickets;
